import React, {useEffect} from 'react';
import {
  Col,
  Row,
  UiBox,
  UiDivider,
  UiFormControl,
  UiFormErrorMessage,
  UiFormLabel,
  UiImage,
  UiInput,
  UiPageLoading,
  UiQuestionnaireLayout,
  UiText,
} from '~/components/elements';
import {
  METHOD_KEYS,
  WEIGHT_LOSS_KEYS,
  WEIGHT_LOSS_ONE_QUESTIONNAIRES,
  WEIGHT_LOSS_TWO_KEYS,
  WEIGHT_LOSS_TWO_QUESTIONNAIRES,
} from '~/constants/medical-questionnaires';
import clock from '~/assets/images/clock.svg';
import {AppDispatch} from '~/redux/root-store';
import {useDispatch, useSelector} from 'react-redux';
import {getMdqStatus, submitOrLogMdq} from '~/redux/questionnaires/thunk';
import {RootState} from '~/redux/root-reducer';
import {setMedicalUUID, setMethodKey, setNextQuestionnaire, setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {useHistory} from 'react-router-dom';
import {PAGE_URLS} from '~/constants';
import welcome1 from '~/assets/images/welcome-1.png';
import {UiCard} from '~/components/elements/UiCard';
import {useInput, useNotification, useRequestState} from '~/hooks';
import {ReviewList} from '~/components/shared/ReviewList';
import {MDQ2Params} from '~/types/models/questionnaire';
import {requestOK} from 'redux-thunk-kit';
import {unwrapResult} from '@reduxjs/toolkit';
import {isBase64} from '~/utils/helpers';
const queryString = require('query-string');

interface Props {}

const MDQ2Questionnaire1Screen = ({}: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const history = useHistory();
  const mdq = useSelector((state: RootState) => state.questionnaires);
  const {methodKey, medicalUUID, answerOrder, collectedData} = mdq;
  const {addMessage} = useNotification(dispatch);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const {loading: checkStatusLoading} = useRequestState(getMdqStatus);
  //   const {wpAuthUser} = medicalQuestionnaire;

  //   const questionnaires = medicalQuestionnaire?.questionnaires;
  const questionnaire = WEIGHT_LOSS_TWO_QUESTIONNAIRES.find(i => i.key === WEIGHT_LOSS_TWO_KEYS.START);

  const currentAnswer = collectedData?.[WEIGHT_LOSS_TWO_KEYS.START]?.data;

  const rules = {
    email: {presence: {allowEmpty: false}, email: true},
    orderNumber: {presence: {allowEmpty: false}},
  };
  const {inputs, setInputs, validation, trimSpaces} = useInput(
    {
      email: currentAnswer?.email || '',
      orderNumber: currentAnswer?.orderNumber || '',
    },
    {rules},
  );

  const onClickSubmit = () => {
    if (validation.getErrors()) {
      return;
    }
    const answerData = {
      key: WEIGHT_LOSS_KEYS.START,
      index: questionnaire?.index || '',
      data: {
        email: inputs.email,
        orderNumber: inputs.orderNumber,
      },
      order: answerOrder,
    };

    dispatch(setQuestionnaireAnswer(answerData));
    dispatch(
      submitOrLogMdq({
        data: {
          uid: medicalUUID,
          methodKey: methodKey.toLowerCase(),
          status: 'draft',
          data: {
            ...collectedData,
            [WEIGHT_LOSS_KEYS.START]: answerData,
          },
        },
      }),
    );
    // dispatch(setNextQuestionnaire({value: WEIGHT_LOSS_KEYS.HOW_IT_WORK}));
    history.push(PAGE_URLS.MDQ2_ADDITIONAL_DETAILS + window.location.search);
  };

  useEffect(() => {
    const fetchData = async () => {
      const location = window.location;
      const query: MDQ2Params = queryString.parse(location.search);
      if (query.payload && isBase64(query.payload)) {
        const decodedData: {email: string; orderNumber: string} = JSON.parse(atob(query.payload));
        setInputs({
          email: decodedData?.email,
          orderNumber: decodedData?.orderNumber,
        });
      }
      const mdq2UUID = query.uid;
      if (mdq2UUID) {
        dispatch(setMedicalUUID({uid: mdq2UUID}));
        dispatch(setMethodKey({key: METHOD_KEYS.MDQ2}));
        const statusCheckRes = await dispatch(getMdqStatus({id: mdq2UUID || ''}));

        if (requestOK(statusCheckRes, getMdqStatus)) {
          const statusResult = unwrapResult(statusCheckRes);
          if (statusResult?.status === 'submitted' || statusResult?.status === 'expired') {
            history.push(PAGE_URLS.MDQ2_INVALID + `?status=${statusResult?.status}`);
            return;
          }
        }
      } else {
        history.push(PAGE_URLS.MDQ2_INVALID + `?status=expired`);
      }
    };
    fetchData();
  }, [addMessage, dispatch, history, setInputs]);

  if (checkStatusLoading) {
    return <UiPageLoading />;
  }

  return (
    <UiQuestionnaireLayout
      actions={questionnaire?.actions}
      handleOnNext={onClickSubmit}
      showBackButton={false}
      pageTitle={questionnaire?.title}
      disableNext={!inputs.email || !inputs.orderNumber || !!errorMessage || checkStatusLoading}
      loading={checkStatusLoading}
      isFirstScreen
    >
      <Col alignItems="center" textAlign="center" mt={6} mb={1}>
        <UiText fontSize="2xl" fontWeight="bold" mb={2}>
          {questionnaire?.questions?.default}
        </UiText>
        <Row alignItems="center" justifyContent="center" mb={12}>
          <UiImage boxSize="0.9375rem" url={clock} />
          <UiText fontWeight="bold" fontSize="xs" px={3}>
            {questionnaire?.questions?.estimateTime}
          </UiText>
        </Row>
        <UiText textAlign="center" fontSize="sm" maxW={72}>
          {questionnaire?.questions?.description}
        </UiText>
        <UiCard
          w="100%"
          // mb={16}
          mt={10}
          borderRadius="1rem"
          boxShadow="0px 6px 25px 0px rgba(0, 0, 0, 0.10)"
          pt={4}
          pb={9}
        >
          <UiFormControl id="orderNumber" isRequired isInvalid={!!validation?.errors?.orderNumber}>
            <UiFormLabel fontWeight="400" fontSize="xs">
              Order Number
            </UiFormLabel>
            <UiInput
              variant="flushed"
              value={inputs.orderNumber}
              onChange={({target}) => setInputs({orderNumber: target.value})}
              fontWeight="600"
            />
            {validation?.errors?.orderNumber && (
              <UiFormErrorMessage>{validation?.errors?.orderNumber[0]}</UiFormErrorMessage>
            )}
          </UiFormControl>
          <UiFormControl id="email" isRequired isInvalid={!!validation?.errors?.email}>
            <UiFormLabel fontWeight="400" fontSize="xs">
              Email Address
            </UiFormLabel>
            <UiInput
              variant="flushed"
              value={inputs.email}
              onChange={({target}) => setInputs({email: target.value})}
              fontWeight="600"
            />
            {validation?.errors?.email && <UiFormErrorMessage>{validation?.errors?.email[0]}</UiFormErrorMessage>}
          </UiFormControl>
        </UiCard>
        {/* <UiDivider color="black" mb={6} px={1} /> */}
        {/* <ReviewList reviewList={questionnaire?.userReviews} /> */}
        {errorMessage && (
          <UiText fontSize="xs" color="red" mt={4}>
            {errorMessage}
          </UiText>
        )}
      </Col>
    </UiQuestionnaireLayout>
  );
};

export default MDQ2Questionnaire1Screen;
